import {
	ACTION_SHORTCUT_OPTIONS,
	BOOKING_TYPE_FILTER,
	CHART_ITEM_TYPE,
	CHART_ROW_TYPE,
	DEFAULT_BOOKING_AMOUNT,
	RENDER_TYPE_SPLIT,
	RESOURCE_PROPERTY,
	resourcePlanningComponentsRequiringPermissions,
} from '../../constants';
import {
	type ActionShortcutOption,
	type ResourcePlanningComponent,
	type ResourcePlanningGantt as Gantt,
	type ResourcePlanningItem,
	type UserAvailability,
	type VueAppElement,
	type ChartSetting,
	type BookingTypeFilter,
	type ResourcePlanningGantt,
} from '../../types';
import { type ResourcePlanningGanttConfig } from './ResourcePlanningGanttConfig';
import { type ResourcePlanningUserSelectProps } from '../ResourcePlanningUserSelect/ResourcePlanningUserSelect.vue';
import { type ResourcePlanningBookingModalProps } from '../ResourcePlanningBookingModal';
import { addEventListener } from './ResourcePlanningGanttGlobalEventListeners';
import { useStorage } from '@vueuse/core';
import type { NotWorthIt } from '@/types/utils';
import { createApp, h, type App, type Component } from 'vue';
import { i18n } from '@/lib/i18n';
import pinia from '@/mfeCore/pinia';
import FloatingVue from 'floating-vue';
import { popoverOptions } from '@/lib/floatingVue';
import {
	type Options,
	createPopper,
	type Instance as Popper,
} from '@popperjs/core';
import { WEEK_STARTS_ON } from '@/constants/settings';
import {
	addDays,
	eachDayOfInterval,
	isBefore,
	isSameDay,
	isWithinInterval,
} from 'date-fns';
import {
	BookingAmountType,
	type User,
	type ResourcePlanningQuery,
	type Role,
	TYPES,
	type RoleProject,
	type Project,
} from '@/generated/graphql';
import { resetTime } from '@/utils/formatting';
import { dateToMysqlString } from '@/composables/settings/useFormatter/formatterUtils';
import { addUserToLoadAvailabilityFor } from '@/features/Availability';
import { resourcePlanningComponents } from '@/features/ResourcePlanning';
import { getUniqueId } from '@/utils/misc';
import { type AppConfirmationPopoverProps } from '@/features/App';
import { type ResourcePlanningProjectsPopoverProps } from '../ResourcePlanningProjectsPopover';
import { isWeekend } from '@/utils/time';
import { type ResourcePlanningPortfolioAddToProjectProps } from '../ResourcePlanningPortfolio/ResourcePlanningPortfolioAddToProject.vue';
import { useSetUpPermissions, useTranslation } from '@/composables';
import { type SAPIDate } from '@/types/sapi/scalars';
import { useMainStore } from '@/stores/main';

const getUnitsBetween = (
	from: string,
	to: string,
	unit: string,
	step: number,
	gantt: Gantt
) => {
	const end = new Date(to);
	let start = new Date(from);
	let units = 0;

	while (start.getTime() < end.getTime()) {
		units++;
		start = gantt.date.add(start, step, unit);
	}

	return units;
};

export const getZoomToFitZoomLevel = (config: ResourcePlanningGanttConfig) => {
	const project = config.gantt.getSubtaskDates();
	const areaWidth = config.gantt.$task.offsetWidth;
	const scaleConfigs = config.getAllScaleConfigLevels();
	let zoomLevel;

	if (project && 'start_date' in project && 'end_date' in project) {
		for (zoomLevel = scaleConfigs.length - 1; zoomLevel > 0; zoomLevel--) {
			const columnCount = getUnitsBetween(
				project.start_date,
				project.end_date,
				scaleConfigs[zoomLevel].unit,
				scaleConfigs[zoomLevel].step,
				config.gantt
			);

			if (
				(columnCount + 2) *
					(scaleConfigs[zoomLevel].min_column_width ?? 0) <=
				areaWidth
			) {
				break;
			}
		}

		zoomLevel =
			zoomLevel >= scaleConfigs.length
				? scaleConfigs.length - 1
				: zoomLevel;
	}

	return zoomLevel;
};

const storage = useStorage(
	'resourcePlanningSettings',
	{} as Record<string, Record<string, string | number | boolean>>
);

export const saveResourcePlanningSetting = (
	projectId: Project['id'],
	setting: ChartSetting,
	value: string | number | boolean
) => {
	if (!storage.value[projectId]) {
		storage.value[projectId] = {};
	}

	storage.value[projectId][setting] = value;
};

export const getResourcePlanningSetting = (
	projectId: string,
	setting: ChartSetting
): string | number | undefined | boolean => {
	return storage.value[projectId]?.[setting];
};

const cachedVueInstances: Record<string, App<Element>> = {};
const vueInstances: App<Element>[] = [];

export const renderComponent = (
	element: string | HTMLElement | null,
	component: ResourcePlanningComponent,
	options: Record<string, NotWorthIt>
) => {
	let tmpElement: HTMLElement | null = null;

	const tmpOptions = { ...options };
	delete tmpOptions.config; // Optimize by removing recursive calls

	const componentId = JSON.stringify({
		component,
		...tmpOptions,
	});

	if (!element) {
		if (cachedVueInstances[componentId]) {
			return cachedVueInstances[componentId];
		}

		tmpElement = document.createElement('div');
		document.body.append(tmpElement);
	} else if (typeof element === 'string') {
		tmpElement = document.querySelector(element) as HTMLElement;
	} else {
		tmpElement = element;
	}

	if ((tmpElement as VueAppElement)?.__vue_app__) {
		return (tmpElement as VueAppElement).__vue_app__;
	}

	const app = createApp({
		name: component,
		setup() {
			if (element) {
				vueInstances.push(app);
			} else {
				tmpElement?.remove();
			}

			if (!cachedVueInstances[componentId]) {
				cachedVueInstances[componentId] = app;
			}

			if (
				resourcePlanningComponentsRequiringPermissions.includes(
					component
				)
			) {
				useSetUpPermissions();
			}

			useMainStore().$patch({
				translation: useTranslation(),
			});

			return () =>
				h(resourcePlanningComponents[component] as Component, options);
		},
	});
	app.use(i18n);
	app.use(pinia);
	app.use(FloatingVue, popoverOptions);
	app.mount(tmpElement);

	return app;
};

export const addPlaceholderUnderProject = (
	config: ResourcePlanningGanttConfig
) => {
	const gantt = config.gantt;
	const utils = renderComponent(null, 'ResourcePlanningAddMember', {
		iconName: 'icon-add-16',
		text: `${config.t('resource_planning_add_member')}...`,
	});

	const placeholder: ResourcePlanningItem = {
		id: config.placeholderId,
		item_type: CHART_ITEM_TYPE.PLACEHOLDER,
		text: `<div class="js-add-project-member" data-placement="top-start">${
			utils?._container?.innerHTML ?? ''
		}</div>`,
		[RESOURCE_PROPERTY]: config.chartProjectId,
		parent: config.chartProjectId,
	};

	config.addItemToChart(
		placeholder,
		config.chartProjectId,
		false,
		gantt.getTaskCount()
	);
	gantt.render();
};

export const getChartBookingListId = (
	resourceId: User['id'] | RoleProject['id'],
	type: typeof TYPES.User | typeof TYPES.RoleProject = TYPES.User
) => {
	return `${CHART_ITEM_TYPE.BOOKING_LIST}-${type}-${resourceId}`;
};

export const getChartResourceId = (
	resourceId:
		| User['id']
		| RoleProject['id']
		| Role['id']
		| Project['id']
		| number,
	type:
		| typeof TYPES.User
		| typeof TYPES.RoleProject
		| typeof CHART_ITEM_TYPE.ADD_TO_PROJECT
		| typeof CHART_ITEM_TYPE.ROLE_DIRECTORY
		| typeof TYPES.Project
		| typeof TYPES.Role = TYPES.User,
	secondaryId: User['id'] = '',
	secondaryType:
		| typeof TYPES.User
		| typeof TYPES.RoleProject
		| undefined = undefined
) => {
	return `${CHART_ITEM_TYPE.RESOURCE}${secondaryId}${
		secondaryType ? secondaryType : ''
	}-${type}-${resourceId}`;
};

export const getResourceType = (chartResourceId: string) => {
	const type = chartResourceId.split('-').slice(-2)[0];

	return type as
		| typeof TYPES.User
		| typeof TYPES.RoleProject
		| typeof TYPES.Role;
};

export const getResourceId = (chartResourceId: string) => {
	return chartResourceId.split('-').pop()?.toString() as
		| User['id']
		| RoleProject['id'];
};

export const cleanUpVueInstances = () => {
	for (let i = vueInstances.length - 1; i >= 0; i--) {
		const app = vueInstances[i];
		if (!document.body.contains(app._container)) {
			app.unmount();
			vueInstances.splice(i, 1);
		}
	}
};

const createFloatingVueWrapper = () => {
	const innerElem = document.createElement('div');
	innerElem.className = 'v-popper__inner';

	const arrow = document.createElement('div');
	arrow.className = 'v-popper__arrow-container';
	arrow.setAttribute('data-popper-arrow', '');

	const arrowInner = document.createElement('div');
	arrowInner.className = 'v-popper__arrow-outer';

	const arrowOuter = document.createElement('div');
	arrowOuter.className = 'v-popper__arrow-inner';

	arrow.append(arrowOuter);
	arrow.append(arrowInner);

	const elem = document.createElement('div');
	elem.className = 'v-popper--theme-dropdown v-popper__popper';

	elem.append(innerElem);
	elem.append(arrow);
	document.body.append(elem);

	return { elem, innerElem, arrow };
};

let popOver: {
	popper?: Popper;
	component?: App<Element>;
	popperElem?: HTMLDivElement;
} = {};

export const closePopOver = () => {
	const { popper, component, popperElem } = popOver;

	(
		component?._container as VueAppElement | undefined
	)?.__vue_app__?.unmount();
	popper?.destroy();
	popperElem?.remove();
	popOver = {};
};

type OnCloseHandler = {
	onClose?(): void;
};

export const showPopOver = (
	target: Element,
	component: ResourcePlanningComponent,
	props:
		| (ResourcePlanningBookingModalProps & OnCloseHandler)
		| (ResourcePlanningUserSelectProps & OnCloseHandler)
		| (ResourcePlanningPortfolioAddToProjectProps & OnCloseHandler)
		| (AppConfirmationPopoverProps & {
				onClose?(): void;
				onCancel?(): void;
				onSave?(): void;
		  })
		| (ResourcePlanningProjectsPopoverProps & OnCloseHandler),
	options?: Partial<Options>
) => {
	closePopOver();

	const { elem, innerElem } = createFloatingVueWrapper();

	popOver.popper = createPopper(target, elem, {
		...options,
		modifiers: [
			{
				name: 'offset',
				options: {
					offset: [5, 5],
				},
			},
			{
				name: 'preventOverflow',
				options: {
					altBoundary: true,
				},
			},
		],
	});
	popOver.popperElem = elem;
	popOver.component = renderComponent(innerElem, component, props);
};

export const openBookingModal = (
	target: HTMLElement | null,
	props: ResourcePlanningBookingModalProps & OnCloseHandler,
	options?: Partial<Options>
) => {
	const CENTER_FIXED_ELEMENT_ID =
		'resource-planning-booking-booking-modal-center-fixed-element';
	const existingCenterElement = document.getElementById(
		CENTER_FIXED_ELEMENT_ID
	);

	let targetElement = target || existingCenterElement;

	if (!targetElement) {
		const centerElement = document.createElement('div');

		centerElement.id = CENTER_FIXED_ELEMENT_ID;
		centerElement.style.position = 'fixed';
		centerElement.style.top = 'calc(50% + 140px)';
		centerElement.style.left = '50%';
		centerElement.style.transform = 'translate(-50%, -50%)';

		document.body.appendChild(centerElement);

		targetElement = centerElement;
	}

	showPopOver(
		targetElement,
		'ResourcePlanningBookingModal',
		{
			...props,
			onClose() {
				closePopOver();
			},
		},
		options
	);
};

export const openSplitBookingModal = (
	props: ResourcePlanningBookingModalProps
) => {
	openBookingModal(null, props, { placement: 'top' });
};

export const openConfirmationModal = (
	props: AppConfirmationPopoverProps & {
		onCancel?(): void;
		onConfirm?(): void;
	},
	options?: Partial<Options>
) => {
	const BOTTOM_CENTER_FIXED_ELEMENT_ID =
		'resource-planning-booking-confirmation-modal-center-fixed-element';
	const existingCenterElement = document.getElementById(
		BOTTOM_CENTER_FIXED_ELEMENT_ID
	);

	let targetElement = existingCenterElement;

	if (!targetElement) {
		const centerElement = document.createElement('div');

		centerElement.id = BOTTOM_CENTER_FIXED_ELEMENT_ID;
		centerElement.style.position = 'fixed';
		centerElement.style.bottom = '4rem';
		centerElement.style.left = '50%';
		centerElement.style.transform = 'translate(-50%, -4rem)';

		document.body.appendChild(centerElement);

		targetElement = centerElement;
	}

	showPopOver(
		targetElement,
		'AppConfirmationPopover',
		{
			...props,
			onCancel() {
				closePopOver();
			},
		},
		{
			placement: 'bottom-end',
			...options,
		}
	);
};

export const openProjectsPopover = (
	target: HTMLElement,
	props: ResourcePlanningProjectsPopoverProps & OnCloseHandler,
	options?: Partial<Options>
) => {
	showPopOver(
		target,
		'ResourcePlanningProjectsPopover',
		{
			...props,
			onClose() {
				closePopOver();
			},
		},
		{ placement: 'top', ...options }
	);
};

export const openProjectMemberDropdown = (
	target: HTMLElement,
	props: ResourcePlanningUserSelectProps & {
		onUserSelected: (id: User['id'], name: User['name']) => void;
		onRoleSelected?: (id: Role['id'], name: Role['name']) => void;
		onClose?(): void;
	},
	options?: Partial<Options>
) => {
	showPopOver(
		target,
		'ResourcePlanningUserSelect',
		{
			...props,
			onClose() {
				closePopOver();
			},
		},
		options
	);
};

export const openUserTimeOffListPopover = (
	target: HTMLElement,
	props: {
		userIds: Array<User['id']>;
		startDate: SAPIDate;
		endDate?: SAPIDate;
	},
	options?: Partial<Options>
) => {
	showPopOver(
		target,
		'TimeOffListPopover',
		{
			...props,
			onClose() {
				closePopOver();
			},
		},
		options
	);
};

export const fixTooltipPositioning = (gantt: Gantt) => {
	const tooltips = gantt.ext.tooltips;
	tooltips.tooltip._calculateTooltipPosition = (event: MouseEvent) => {
		let target: HTMLElement | null = event.target as HTMLElement;

		if (!target) {
			return;
		}

		const tooltipClass = 'gantt_task_content';
		const useParentAsTooltipTarget =
			!target.classList.contains(tooltipClass) &&
			target.closest(`.${tooltipClass}`);

		if (useParentAsTooltipTarget) {
			target =
				target
					.closest('.gantt_task_line')
					?.querySelector(`.${tooltipClass}`) ?? null;
		}

		if (target) {
			const tooltipNode = tooltips.tooltip.getNode();
			const targetBounds = target.getBoundingClientRect();
			const targetTop = targetBounds.top;
			const targetHeight = targetBounds.height;
			const makeTheTooltipArrowTouchTheTarget = 2;

			// ganttChart.es6 has similar fix
			if (targetBounds.width === 0 && targetHeight == 0) {
				// Handle edge-case where the targeted element gets removed from the DOM.
				// In this case we don't render the tooltip and let the next rendering tick setup new event handlers.
				return false;
			}

			const top =
				targetTop +
				window.scrollY -
				targetHeight +
				makeTheTooltipArrowTouchTheTarget;

			const left =
				Math.max(
					Math.min(event.clientX, targetBounds.right),
					targetBounds.left
				) -
				tooltipNode.offsetWidth / 2;

			return { top, left };
		}

		return false;
	};
};
export const getRemainingAvailabilityDuration = ({
	startDate,
	endDate,
	userAvailability,
	resourceType,
	includeWeekends,
}: {
	startDate: Date;
	endDate: Date;
	userAvailability: UserAvailability;
	resourceType: typeof TYPES.User | typeof TYPES.RoleProject;
	includeWeekends?: boolean;
}) => {
	let availability = 0;
	let daysUsed = 0;

	const datesList = isSameDay(startDate, endDate)
		? [startDate]
		: eachDayOfInterval({
				start: startDate,
				end: endDate,
			});

	if (resourceType === TYPES.RoleProject) {
		datesList.forEach((date) => {
			if (includeWeekends || !isWeekend(date)) {
				daysUsed++;

				availability += DEFAULT_BOOKING_AMOUNT.TIME_PER_DAY;
			}
		});

		return {
			availability,
			daysUsed,
		};
	}

	datesList.forEach((date) => {
		const dayAvailability = userAvailability[dateToMysqlString(date)];
		availability += dayAvailability;
	});

	return {
		availability,
		daysUsed,
	};
};

export const getTotalBookingDuration = ({
	startDate,
	endDate,
	bookingType,
	bookedDuration,
	bookedPercent,
	userAvailability,
	resourceId,
	resourceType,
	includeWeekends,
}: {
	startDate: Date;
	endDate: Date;
	bookingType: BookingAmountType | undefined;
	bookedDuration: number;
	bookedPercent: number;
	userAvailability: UserAvailability;
	resourceId: User['id'] | RoleProject['id'];
	resourceType:
		| typeof TYPES.User
		| typeof TYPES.RoleProject
		| typeof TYPES.Role;
	includeWeekends: boolean;
}) => {
	let duration = 0;
	let daysUsed = 0;

	const datesList = isSameDay(startDate, endDate)
		? [startDate]
		: eachDayOfInterval({
				start: startDate,
				end: endDate,
			});

	// Role availability
	if (resourceType === TYPES.RoleProject || resourceType === TYPES.Role) {
		datesList.forEach((date) => {
			if (includeWeekends || !isWeekend(date)) {
				daysUsed++;

				if (bookingType === BookingAmountType.Hour) {
					duration += bookedDuration;
				} else if (bookingType === BookingAmountType.Percent) {
					duration +=
						(DEFAULT_BOOKING_AMOUNT.TIME_PER_DAY * bookedPercent) /
						100;
				} else {
					duration = bookedDuration;
				}
			}
		});

		return {
			duration,
			daysUsed,
		};
	}

	// User availability
	datesList.forEach((date) => {
		const availability = userAvailability[dateToMysqlString(date)];

		if (availability > 0) {
			daysUsed++;

			if (bookingType === BookingAmountType.Hour) {
				duration += bookedDuration;
			} else if (bookingType === BookingAmountType.Percent) {
				duration += (availability * bookedPercent) / 100;
			} else {
				duration = bookedDuration;
			}
		}

		if (availability === undefined) {
			addUserToLoadAvailabilityFor(resourceId, date);
		}
	});

	return {
		duration,
		daysUsed,
	};
};

export const areBookingDatesOverlapping = (
	source: ResourcePlanningItem,
	target: ResourcePlanningItem
) => {
	const sourceStartDate = resetTime(source.start_date ?? '');
	let sourceEndDate = resetTime(source.end_date ?? '');

	if (
		isBefore(sourceEndDate, sourceStartDate) ||
		isSameDay(sourceStartDate, sourceEndDate)
	) {
		sourceEndDate = addDays(sourceStartDate, 1);
	}

	const sourceInterval = {
		start: sourceStartDate,
		end: addDays(sourceEndDate, -1),
	};

	const targetStartDate = resetTime(target.start_date ?? '');
	let targetEndDate = resetTime(target.end_date ?? '');

	if (
		isBefore(targetEndDate, targetStartDate) ||
		isSameDay(targetStartDate, targetEndDate)
	) {
		targetEndDate = addDays(targetStartDate, 1);
	}

	const targetInterval = {
		start: targetStartDate,
		end: addDays(targetEndDate, -1),
	};

	return (
		isWithinInterval(sourceInterval.start, targetInterval) ||
		isWithinInterval(sourceInterval.end, targetInterval) ||
		isWithinInterval(targetInterval.start, sourceInterval) ||
		isWithinInterval(targetInterval.end, sourceInterval)
	);
};

export const getProjectPlannedDates = (
	project: ResourcePlanningQuery['Project']
) => {
	const startDate = resetTime(project?.date ?? '');
	const endDate = project?.deadline ? resetTime(project?.deadline) : null;

	return {
		startDate,
		endDate,
	};
};

export const weekStartsOnSettingDateFnsMapping: Record<
	number,
	0 | 1 | 2 | 3 | 4 | 5 | 6
> = {
	[WEEK_STARTS_ON.SATURDAY]: 6,
	[WEEK_STARTS_ON.SUNDAY]: 0,
	[WEEK_STARTS_ON.MONDAY]: 1,
};

export const isBookingVisible = (
	filterValue: BookingTypeFilter,
	isTentative?: boolean,
	projectId?: string
) => {
	if (projectId) {
		if (filterValue === BOOKING_TYPE_FILTER.FIXED_AND_TENTATIVE) {
			return true;
		}

		return isTentative
			? filterValue === BOOKING_TYPE_FILTER.TENTATIVE
			: filterValue === BOOKING_TYPE_FILTER.FIXED;
	}

	return true;
};

export const convertHexToHSL = (hex: string, lightnessLevel?: number) => {
	// Convert hex to RGB
	let r = 0;
	let g = 0;
	let b = 0;

	if (hex.length === 4) {
		r = parseInt(hex[1] + hex[1], 16);
		g = parseInt(hex[2] + hex[2], 16);
		b = parseInt(hex[3] + hex[3], 16);
	} else if (hex.length === 7) {
		r = parseInt(hex.slice(1, 3), 16);
		g = parseInt(hex.slice(3, 5), 16);
		b = parseInt(hex.slice(5, 7), 16);
	}

	// Convert RGB to HSL
	r /= 255;
	g /= 255;
	b /= 255;
	const cmin = Math.min(r, g, b);
	const cmax = Math.max(r, g, b);
	const delta = cmax - cmin;
	let h;

	if (delta === 0) {
		h = 0;
	} else if (cmax === r) {
		h = ((g - b) / delta) % 6;
	} else if (cmax === g) {
		h = (b - r) / delta + 2;
	} else {
		h = (r - g) / delta + 4;
	}

	h = Math.round(h * 60);

	if (h < 0) {
		h += 360;
	}

	let l = (cmax + cmin) / 2;
	let s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));
	s = +(s * 100).toFixed(1);

	if (lightnessLevel) {
		l = lightnessLevel / 100;
	}

	l = +(l * 100).toFixed(1);

	return `hsl(${h}, ${s}%, ${l}%)`;
};

export const getIconHTML = (icon: string, fill: string) =>
	`<svg class="sui-icon sui-icon--24" style="fill: ${fill}"><use xlink:href="#${icon}"></use></svg>`;

type ProjectUserOrRole =
	| NonNullable<ResourcePlanningQuery['Project']>['members'][0]
	| NonNullable<
			NonNullable<ResourcePlanningQuery['Project']>['roleProjects']
	  >[0];

export const getResourceAndBookingsRow = (
	userOrRole: ProjectUserOrRole,
	config: ResourcePlanningGanttConfig
): ResourcePlanningItem[] => {
	const mainGroupId = getUniqueId();

	if (userOrRole.__typename === TYPES.User) {
		const user = userOrRole;

		return [
			{
				group_id: mainGroupId,
				item_type: CHART_ITEM_TYPE.RESOURCE,
				row_type: CHART_ROW_TYPE.HEATMAP,
				id: getChartResourceId(user.id),
				text: user.name,
				user_avatar: user.profilePicture?.mini,
				color: user.hslColor,
				initials: user.initials,
				role_name: user.role?.name,
				[RESOURCE_PROPERTY]: config.chartProjectId,
				parent: config.chartProjectId,
			},
			{
				group_id: getChartResourceId(user.id),
				id: getChartBookingListId(user.id),
				text: config.t('resource_planning_booked_duration'),
				item_type: CHART_ITEM_TYPE.BOOKING_LIST,
				row_type: CHART_ROW_TYPE.BOOKINGS,
				[RESOURCE_PROPERTY]: mainGroupId,
				parent: mainGroupId,
				user_id: user.id,
				render: RENDER_TYPE_SPLIT,
			},
		];
	} else if (userOrRole.__typename === TYPES.RoleProject) {
		const roleProject = userOrRole;

		return [
			{
				group_id: mainGroupId,
				item_type: CHART_ITEM_TYPE.ROLE,
				row_type: CHART_ROW_TYPE.HEATMAP,
				id: getChartResourceId(roleProject.id, TYPES.RoleProject),
				text: config.t('role_placeholder', {
					role: roleProject.role.name,
				}),
				role_id: roleProject.role.id,
				[RESOURCE_PROPERTY]: config.chartProjectId,
				parent: config.chartProjectId,
			},
			{
				group_id: getChartResourceId(roleProject.id, TYPES.RoleProject),
				role_id: roleProject.role.id,
				id: getChartBookingListId(roleProject.id, TYPES.RoleProject),
				text: config.t('resource_planning_booked_duration'),
				item_type: CHART_ITEM_TYPE.BOOKING_LIST,
				row_type: CHART_ROW_TYPE.BOOKINGS,
				[RESOURCE_PROPERTY]: mainGroupId,
				parent: mainGroupId,
				render: RENDER_TYPE_SPLIT,
			},
		];
	}

	return [];
};

export const getProjectPopoverEntityProps = (
	resource: ResourcePlanningItem,
	gantt: ResourcePlanningGantt
) => {
	const userIds: string[] = [];
	const roleIds: string[] = [];
	const entityId = getResourceId(resource.id);
	const entityType = getResourceType(resource.id);

	if (resource.item_type === CHART_ITEM_TYPE.ROLE_DIRECTORY) {
		gantt.eachTask((item) => {
			const { userIds: tmpUserIds, roleIds: tmpRoleIds } =
				getProjectPopoverEntityProps(item, gantt);

			userIds.push(...tmpUserIds);
			roleIds.push(...tmpRoleIds);
		}, resource.id);
	} else {
		if (entityType === TYPES.Role) {
			roleIds.push(entityId);
		} else if (entityType === TYPES.User) {
			userIds.push(entityId);
		}
	}

	return {
		entityType,
		roleIds,
		userIds,
	};
};

export const addTimeOffHighlightClickEventHandler = () =>
	addEventListener('click', '.js-open-time-off', (e) => {
		const popOverTarget = (e.target as Element)?.closest(
			'.js-time-off-element'
		) as HTMLElement | null;

		if (!popOverTarget) {
			return;
		}

		const { userId, startDate, endDate } = popOverTarget.dataset;

		if (!userId || !startDate || !endDate) {
			return;
		}

		openUserTimeOffListPopover(
			popOverTarget,
			{
				userIds: [userId],
				startDate: startDate,
				endDate: endDate,
			},
			{
				placement: 'top',
			}
		);
	});

export const getActionOptions = ({
	item,
	entityType,
	hasBookings,
	canRemoveProjectMember,
}: {
	item: ResourcePlanningItem;
	entityType: typeof TYPES.RoleProject | typeof TYPES.User;
	hasBookings: boolean;
	canRemoveProjectMember: boolean;
}): ActionShortcutOption[] => {
	const options: ActionShortcutOption[] = [
		ACTION_SHORTCUT_OPTIONS.NEW_BOOKING,
	];

	if (canRemoveProjectMember) {
		options.push(ACTION_SHORTCUT_OPTIONS.REMOVE_PROJECT_MEMBER);
	}

	if (hasBookings) {
		if (entityType === TYPES.User) {
			options.push(ACTION_SHORTCUT_OPTIONS.MARK_ALL_FIXED);
		}
		options.push(
			ACTION_SHORTCUT_OPTIONS.MOVE_BOOKINGS,
			ACTION_SHORTCUT_OPTIONS.CLEAR_BOOKINGS
		);
	}

	if (entityType === TYPES.RoleProject || item.role_project_id) {
		options.push(ACTION_SHORTCUT_OPTIONS.ASSIGN_MEMBER);
	}

	return options;
};
